<script setup>
import {computed, onBeforeUnmount, onMounted, ref, defineModel, defineProps, defineEmits} from 'vue'
import lottie from 'lottie-web'
import {findInvalidEmails, parseStringCommaJoinToArray} from '@shared/utils/helpers'
import successAnimationData from '@shared-assets/animations/success-confetti.json'
import PaymentMethodDetails from './PaymentMethodDetails.vue'

const email = defineModel('email', {default: ''})
const managers = defineModel('managers', {default: ''})
const props = defineProps({
  title: {
    type: String,
    default: 'Proforma invoice successfully generated',
  },
  paymentType: {
    type: [String, undefined],
    default: undefined,
  },
  requisites: {
    type: String,
    default: '',
  },
  requisitesTag: {
    type: Object,
    default: null,
  },
  requisitesLoading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  withEmail: {
    type: Boolean,
    default: true,
  },
  emailSent: {
    type: Boolean,
    default: false,
  },
  withManagers: {
    type: Boolean,
    default: true,
  },
  sentToEmail: {
    type: String,
    default: '',
  },
})
const emit = defineEmits(['download', 'send-email', 'send-managers'])

const invalidEmails = computed(() => {
  return findInvalidEmails(parseStringCommaJoinToArray(email.value || ''))
})
const invalidManagers = computed(() => {
  return findInvalidEmails(parseStringCommaJoinToArray(managers.value || ''))
})
const canSendEmail = computed(() => {
  return !props.disabled && !!email.value && invalidEmails.value.length === 0
})
const canSendManagers = computed(() => {
  return !props.disabled && !!managers.value && invalidManagers.value.length === 0
})
const paymentHint = computed(() => {
  return props.sentToEmail
    ? 'You can also make the payment using the details below.'
    : 'You can make the payment using the details below'
})

const animationContainer = ref(null)
let successAnimation = null

onMounted(() => {
  successAnimation = lottie.loadAnimation({
    container: animationContainer.value,
    renderer: 'svg',
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
  })
})

onBeforeUnmount(() => {
  successAnimation?.destroy()
})

const onDownload = () => {
  emit('download')
}

const onSendEmail = () => {
  emit('send-email')
}

const onSendManagers = () => {
  emit('send-managers')
}
</script>

<template>
  <div class="proforma-success">
    <div class="proforma-success__header custom mb-7">
      <div ref="animationContainer" class="proforma-success__animation"></div>
      <h5 class="proforma-success__title custom fs-7 mb-2">{{ props.title }}</h5>
      <p v-if="props.sentToEmail" class="proforma-success__subtitle custom mb-2">
        A copy has been sent to {{ props.sentToEmail }}.
      </p>
      <p class="proforma-success__subtitle mb-0">{{ paymentHint }}</p>
    </div>

    <payment-method-details
      :payment-type="props.paymentType"
      :requisites="props.requisites"
      :tag="props.requisitesTag"
      :loading="props.requisitesLoading"
    />

    <div class="d-flex custom gap-7 mt-7">
      <div class="flex-grow-1">
        <h6 class="proforma-success__block-title custom mb-2">Download Invoice</h6>
        <p class="proforma-success__subtitle custom mb-4">Save a PDF copy of your invoice</p>
        <button type="button" class="btn btn-secondary w-100" :disabled="props.disabled" @click="onDownload">
          Download PDF <i class="fa-light fa-arrow-down-to-line ml-2" />
        </button>
      </div>

      <div v-if="props.withEmail" class="flex-grow-1">
        <h6 class="proforma-success__block-title custom mb-2">Email Invoice</h6>
        <p class="proforma-success__subtitle custom mb-4">Send the invoice to your email address</p>
        <div class="d-flex custom gap-3">
          <input v-model="email" class="form-control" placeholder="Enter email" :disabled="props.disabled" />
          <button type="button" class="btn btn-secondary text-nowrap" :disabled="!canSendEmail" @click="onSendEmail">
            Send
          </button>
        </div>
        <div v-if="invalidEmails.length > 0" class="text-danger custom mt-2 fs-4">
          Invalid email format: {{ invalidEmails.join(', ') }}
        </div>
      </div>

      <div v-if="props.withManagers" class="flex-grow-1">
        <h6 class="proforma-success__block-title custom mb-2">Managers (CC)</h6>
        <p class="proforma-success__subtitle custom mb-4">Send the invoice to Managers (CC) via email</p>
        <div class="d-flex custom gap-3">
          <input v-model="managers" class="form-control" placeholder="Enter emails" :disabled="props.disabled" />
          <button
            type="button"
            class="btn btn-secondary text-nowrap"
            :disabled="!canSendManagers"
            @click="onSendManagers"
          >
            Send
          </button>
        </div>
        <div v-if="invalidManagers.length > 0" class="text-danger custom mt-2 fs-4">
          Invalid email format: {{ invalidManagers.join(', ') }}
        </div>
      </div>
    </div>

    <div v-if="props.emailSent" class="proforma-success__sent custom mt-7">
      The invoice has been sent to the provided email.
    </div>
  </div>
</template>

<style lang="scss" scoped>
.proforma-success__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.proforma-success__animation {
  width: toRem(133);
  height: toRem(133);
  margin: toRem(-22) 0 toRem(-26);
}

.proforma-success__title {
  font-weight: 600;
  line-height: 1.4;
  color: $black;
}

.proforma-success__block-title {
  font-size: toRem(16);
  font-weight: 600;
  line-height: 1.4;
  color: $black;
}

.proforma-success__subtitle {
  font-size: toRem(14);
  line-height: 1.5;
  color: $text-muted;
}

.proforma-success__sent {
  padding: toRem(8) toRem(16);
  border-left: toRem(3) solid $text-dark;
  background: #f8f9fa;
  font-size: toRem(13);
  line-height: 1.5;
  color: $text-dark;
}
</style>
